import { useSession } from '@/hooks/auth/useSession';
import { api } from '@/trpc/react';
import { SUBSCRIPTION_PLAN } from '@acme/lib';

/**
 * Custom hook to check if a user is subscribed to PicStudio Drive
 *
 * This hook provides two ways to check subscription status:
 * 1. From the session data (faster but might not be up-to-date)
 * 2. From the API (more accurate but requires a network request)
 */
export function usePicStudioDriveSubscription() {
  const { user, isLoggedIn } = useSession();
  const subscriptionStatusQuery = api.user.getSubscriptionStatus.useQuery(undefined, {
    // Don't refetch on window focus to reduce API calls
    refetchOnWindowFocus: false,
    // Cache the result for 5 minutes
    staleTime: 5 * 60 * 1000,
    enabled: isLoggedIn
  });

  // Check from session data (faster but might not be up-to-date)
  const isSubscribedFromSession = user?.subscriptionPlan === SUBSCRIPTION_PLAN.picstudio_drive;

  // Check from API (more accurate but requires a network request)
  const isSubscribedFromAPI =
    subscriptionStatusQuery.data?.status === 'active' &&
    subscriptionStatusQuery.data?.plan === SUBSCRIPTION_PLAN.picstudio_drive;

  // Determine if subscription is active based on both sources
  // Prefer API data if available, otherwise fall back to session data
  const isSubscribed = subscriptionStatusQuery.isSuccess ? isSubscribedFromAPI : isSubscribedFromSession;

  // Check if subscription is set to cancel at the end of the current period
  const willCancelAtPeriodEnd = subscriptionStatusQuery.data?.cancelAtPeriodEnd || false;

  // Get the end date of the current subscription period
  const currentPeriodEnd = subscriptionStatusQuery.data?.currentPeriodEnd
    ? new Date(subscriptionStatusQuery.data.currentPeriodEnd)
    : undefined;

  return {
    isSubscribed,
    isSubscribedFromSession,
    isSubscribedFromAPI,
    isLoading: subscriptionStatusQuery.isLoading,
    isError: subscriptionStatusQuery.isError,
    willCancelAtPeriodEnd,
    currentPeriodEnd,
    // Return the raw data for advanced use cases
    subscriptionData: subscriptionStatusQuery.data
  };
}
